'use client';

import { makeObservable, observable, action } from 'mobx';

export type DisplayMode = 'sheet' | 'split' | 'bamboo';

class DisplayModeStore {
  displayMode: DisplayMode = 'sheet';

  constructor() {
    makeObservable(this, {
      displayMode: observable,
      setDisplayMode: action,
      hydrate: action,
      toggle: action,
    });
  }

  /**
   * Restore display mode from localStorage.
   * Must be called from a client-side useEffect (not during SSR or constructor)
   * to avoid React hydration mismatches.
   */
  hydrate() {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('displayMode') as DisplayMode | null;
      if (saved === 'bamboo' || saved === 'split') {
        this.displayMode = saved;
      }
    }
  }

  setDisplayMode(mode: DisplayMode) {
    this.displayMode = mode;
    if (typeof window !== 'undefined') {
      localStorage.setItem('displayMode', mode);
    }
  }

  /** Full-screen bamboo mode */
  get isBamboo() {
    return this.displayMode === 'bamboo';
  }

  /** Split: sheet music top 1/3, bamboo bottom 2/3 */
  get isSplit() {
    return this.displayMode === 'split';
  }

  /** Whether the bamboo renderer should be active (split or bamboo) */
  get showBamboo() {
    return this.displayMode === 'bamboo' || this.displayMode === 'split';
  }

  /** Cycle: sheet → bamboo (full) → split (2/3 bamboo + 1/3 notes) → sheet */
  toggle() {
    const next: Record<DisplayMode, DisplayMode> = {
      sheet: 'bamboo',
      bamboo: 'split',
      split: 'sheet',
    };
    this.setDisplayMode(next[this.displayMode]);
  }
}

const displayModeStore = new DisplayModeStore();
export default displayModeStore;
